import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Caption1,
  Dropdown,
  Input,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Option,
  Spinner,
  Tab,
  TabList,
  Text,
  makeStyles,
} from '@fluentui/react-components';
import { isComplianceTracked, type LifecycleState, type Person, type ReadinessState } from '@vigeo/shared';
import { ShellIcons, shellMedia, shellMetrics, tokens } from '@vigeo/ui';
import {
  CandidateBoard,
  CandidateTable,
  CsvImportPanel,
  directorySorts,
  filterPeople,
  isFiltered,
  lifecyclePresenters,
  noFilters,
  readStoredView,
  roleLabel,
  rolesPresent,
  sortPeople,
  sortPresenters,
  storeView,
  summarize,
  usePeopleReadiness,
  UNTRACKED,
  type DirectoryFilters,
  type DirectorySort,
  type DirectoryView,
} from '@/features/candidates';
import { statePresenters } from '@/features/readiness';
import { fetchCatalogOptions, fetchPeople } from '@/lib/api';
import { fetchAuthState } from '@/lib/auth';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXL },
  /** People-specific header: title and context left, admin intake actions right (Figma 136:3925). */
  head: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: tokens.spacingHorizontalL, flexWrap: 'wrap' },
  titles: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  title: { margin: 0, fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600, fontWeight: tokens.fontWeightBold },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', [shellMedia.phone]: { width: '100%' } },
  action: { [shellMedia.phone]: { flexGrow: 1 } },
  /** Reference card: flat white, radius 16, no border, one tinted shadow. */
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadowCard,
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    alignItems: 'flex-start',
  },
  cardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalM, flexWrap: 'wrap', alignSelf: 'stretch' },
  h2: { margin: 0, fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400, fontWeight: tokens.fontWeightSemibold },
  /** Summary strip: counts that follow from the People response; 28/700 figures over 12px labels. */
  summary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(8rem, 1fr))',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadowCard,
    overflow: 'hidden',
    [shellMedia.phone]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  },
  cell: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderLeft: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    ':first-child': { borderLeft: 'none' },
    [shellMedia.phone]: {
      ':nth-child(odd)': { borderLeft: 'none' },
      ':nth-child(n+3)': { borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}` },
      ':last-child:nth-child(odd)': { gridColumn: '1 / -1' },
    },
  },
  figure: { fontFamily: tokens.fontFamilyNumeric, fontSize: tokens.fontSizeHero700, lineHeight: tokens.lineHeightHero700, fontWeight: tokens.fontWeightBold },
  label: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 },
  /** Search within People, local pickers, then the view switch; wraps onto lines as it narrows. */
  toolbar: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  search: { flexGrow: 1, flexBasis: '14rem', maxWidth: '26rem', [shellMedia.phone]: { maxWidth: 'none', flexBasis: '100%' } },
  // On a phone the picker's button is the touch target, so it takes the accessible minimum (Gate 6).
  picker: { minWidth: '9rem', [shellMedia.phone]: { flexGrow: 1, minWidth: '40%', '& .fui-Dropdown__button': { minHeight: shellMetrics.touchTarget } } },
  tabs: { marginLeft: 'auto', [shellMedia.phone]: { marginLeft: 0, width: '100%' } },
  tab: { minHeight: shellMetrics.controlHeight, [shellMedia.phone]: { minHeight: shellMetrics.touchTarget, flexGrow: 1 } },
  results: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  centered: { display: 'flex', justifyContent: 'center', paddingBlock: tokens.spacingVerticalXXXL },
});

const EMPTY: readonly Person[] = [];
const isSort = (value: string): value is DirectorySort => (directorySorts as readonly string[]).includes(value);

interface PickerOption {
  readonly value: string;
  readonly text: string;
}

function Picker({ label, value, options, onChange, className }: { readonly label: string; readonly value: string; readonly options: readonly PickerOption[]; readonly onChange: (value: string) => void; readonly className: string }) {
  return (
    <Dropdown
      aria-label={label}
      className={className}
      value={options.find((o) => o.value === value)?.text ?? ''}
      selectedOptions={[value]}
      onOptionSelect={(_, data) => onChange(data.optionValue ?? options[0]?.value ?? '')}
    >
      {options.map((o) => (
        <Option key={o.value} value={o.value} text={o.text}>
          {o.text}
        </Option>
      ))}
    </Dropdown>
  );
}

type Intake = 'import' | null;

/**
 * The People directory (checkpoint 7B): one People response, searched, filtered and sorted
 * locally; readiness per person from the same derived checklist the Person Overview reads.
 * Search is within People only. Intake (add, import) is offered to admins and to nobody else.
 */
export function CandidatesPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const auth = useQuery({ queryKey: ['auth'], queryFn: fetchAuthState, retry: false });
  const people = useQuery({ queryKey: ['people'], queryFn: () => fetchPeople() });
  const options = useQuery({ queryKey: ['catalog-options'], queryFn: fetchCatalogOptions, staleTime: Infinity });

  const [view, setView] = useState<DirectoryView>(readStoredView);
  const [filters, setFilters] = useState<DirectoryFilters>(noFilters);
  const [sort, setSort] = useState<DirectorySort>('name-asc');
  /**
   * Add candidate is its own page now (`/app/people/new`), so the only thing that can be routed
   * open here is the importer — which is what Add candidate's own "Import a CSV" links back to.
   * The old `'add'` arm survived a refactor and left the Dashboard's button opening a panel
   * titled "Add a candidate" whose body was the CSV box, with no way to close it.
   */
  const routedIntake = useLocation().state as { readonly intake?: unknown } | null;
  const [intake, setIntake] = useState<Intake>(routedIntake?.intake === 'import' ? 'import' : null);

  // The existing gate, unchanged: only signed-in admins get mutation controls.
  const canEdit = auth.data?.status === 'signed-in' && auth.data.user.isAdmin;

  const all = people.data ?? EMPTY;
  // Staff added by hand are not tracked yet (ADR-016 §4): no checklist is requested for them, and their row says so.
  const tracked = useMemo(() => all.filter(isComplianceTracked), [all]);
  const readiness = usePeopleReadiness(tracked);
  const { stateOf } = readiness;
  const trackedIds = useMemo(() => new Set(tracked.map((person) => person.id)), [tracked]);
  const entryOf = useCallback((id: string) => (trackedIds.has(id) ? readiness.entryOf(id) : UNTRACKED), [trackedIds, readiness]);
  const shown = useMemo(() => sortPeople(filterPeople(all, filters, stateOf), sort, stateOf), [all, filters, sort, stateOf]);
  const summary = useMemo(() => summarize(all), [all]);
  const roles = useMemo(() => rolesPresent(all, options.data), [all, options.data]);
  const roleOf = (person: Person) => roleLabel(person, options.data);

  const set = <K extends keyof DirectoryFilters>(key: K, value: DirectoryFilters[K]) => setFilters((f) => ({ ...f, [key]: value }));
  const selectView = (next: DirectoryView) => {
    setView(next);
    storeView(next);
  };
  const toggleIntake = (which: Exclude<Intake, null>) => setIntake((current) => (current === which ? null : which));

  const stageOptions: PickerOption[] = [
    { value: 'all', text: 'All stages' },
    ...summary.byStage.filter((s) => s.count > 0).map((s) => ({ value: s.state, text: lifecyclePresenters[s.state].label })),
  ];
  const roleOptions: PickerOption[] = [{ value: 'all', text: 'All roles' }, ...roles.map((r) => ({ value: r.key, text: r.label }))];
  const readinessOptions: PickerOption[] = [
    { value: 'all', text: 'Any readiness' },
    ...(Object.keys(statePresenters) as ReadinessState[]).map((state) => ({ value: state, text: statePresenters[state].label })),
  ];
  const sortOptions: PickerOption[] = directorySorts.map((key) => ({ value: key, text: sortPresenters[key].label }));

  const context = `${all.length === 0 ? '' : `${all.length} ${all.length === 1 ? 'person' : 'people'} · `}Everyone the agency tracks, from first application to separation.`;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div className={styles.titles}>
          <h1 className={styles.title}>People</h1>
          <Caption1>{context}</Caption1>
        </div>
        {canEdit ? (
          <div className={styles.actions}>
            <Button
              appearance="primary"
              icon={<ShellIcons.plus />}
              className={styles.action}
              onClick={() => { navigate('/app/people/new'); }}
            >
              Add a candidate
            </Button>
            <Button
              appearance="secondary"
              icon={<ShellIcons.upload />}
              className={styles.action}
              aria-expanded={intake === 'import'}
              aria-controls={intake === 'import' ? 'people-intake' : undefined}
              onClick={() => toggleIntake('import')}
            >
              Import
            </Button>
          </div>
        ) : null}
      </header>

      {canEdit && intake !== null ? (
        <section id="people-intake" className={styles.card} aria-labelledby="people-intake-title">
          <div className={styles.cardHead}>
            <h2 id="people-intake-title" className={styles.h2}>Import from a spreadsheet</h2>
            {/* The importer has no footer of its own, so this is its only dismiss control. */}
            <Button appearance="subtle" onClick={() => setIntake(null)}>
              Close
            </Button>
          </div>
          <CsvImportPanel />
        </section>
      ) : null}

      {all.length > 0 ? (
        <>
          <section className={styles.summary} aria-label="People summary">
            <div className={styles.cell}>
              <span className={styles.figure}>{summary.total}</span>
              <span className={styles.label}>Total people</span>
            </div>
            {summary.byStage
              .filter((s) => lifecyclePresenters[s.state].onBoard || s.count > 0)
              .map((s) => (
                <div key={s.state} className={styles.cell}>
                  <span className={styles.figure}>{s.count}</span>
                  <span className={styles.label}>{lifecyclePresenters[s.state].label}</span>
                </div>
              ))}
            <div className={styles.cell}>
              <span className={styles.figure} aria-busy={!readiness.settled}>
                {readiness.settled ? readiness.needsReview : '…'}
              </span>
              <span className={styles.label}>Needs review</span>
            </div>
          </section>

          <div className={styles.toolbar} role="search" aria-label="Search and filter people">
            <Input
              type="search"
              aria-label="Search people"
              placeholder="Search people by name or email"
              className={styles.search}
              contentBefore={<ShellIcons.search />}
              value={filters.query}
              onChange={(_, data) => set('query', data.value)}
            />
            <Picker label="Stage" className={styles.picker} value={filters.stage} options={stageOptions} onChange={(v) => set('stage', v as LifecycleState | 'all')} />
            <Picker label="Role" className={styles.picker} value={filters.role} options={roleOptions} onChange={(v) => set('role', v)} />
            <Picker label="Readiness" className={styles.picker} value={filters.readiness} options={readinessOptions} onChange={(v) => set('readiness', v as ReadinessState | 'all')} />
            <Picker label="Sort by" className={styles.picker} value={sort} options={sortOptions} onChange={(v) => setSort(isSort(v) ? v : 'name-asc')} />
            <TabList aria-label="View" className={styles.tabs} selectedValue={view} onTabSelect={(_, data) => selectView(data.value === 'list' ? 'list' : 'board')}>
              <Tab value="board" className={styles.tab}>
                Board
              </Tab>
              <Tab value="list" className={styles.tab}>
                List
              </Tab>
            </TabList>
          </div>

          {isFiltered(filters) ? (
            <div className={styles.results} role="status">
              <Caption1>
                Showing {shown.length} of {all.length}
              </Caption1>
              <Button appearance="subtle" size="small" onClick={() => setFilters(noFilters)}>
                Clear
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {people.isPending ? (
        <div className={styles.centered}>
          <Spinner size="small" label="Loading people" />
        </div>
      ) : people.isError ? (
        <MessageBar intent="error">
          <MessageBarBody>Could not load people. Try again in a moment.</MessageBarBody>
          <MessageBarActions>
            <Button onClick={() => void people.refetch()}>Try again</Button>
          </MessageBarActions>
        </MessageBar>
      ) : all.length === 0 ? (
        <section className={styles.card} aria-label="No people">
          <Text weight="semibold">No people yet.</Text>
          <Caption1>{canEdit ? 'Add a candidate or import a spreadsheet to get started.' : 'An HR admin adds candidates; they will appear here.'}</Caption1>
        </section>
      ) : shown.length === 0 ? (
        <section className={styles.card} aria-label="No matches">
          <Text weight="semibold">No one matches.</Text>
          <Caption1>Try a different search, or clear the filters.</Caption1>
          <Button onClick={() => setFilters(noFilters)}>Clear search and filters</Button>
        </section>
      ) : view === 'board' ? (
        <CandidateBoard people={shown} roleOf={roleOf} entryOf={entryOf} />
      ) : (
        <CandidateTable people={shown} roleOf={roleOf} entryOf={entryOf} />
      )}
    </div>
  );
}
